import React from 'react';
import loadable from '@loadable/component';
import useBellaTranslator from 'Client/hooks/useTranslator';
import useConfig from 'AppShell/hooks/useConfig';
import { CountryCode } from 'AppShell/utils/country/getShortenedCountryCode';

const redirectedCountryCodes = {
    At: 'De',
    Be: 'Fr',
    Ch: 'De',
    Pt: 'Nl',
};

export type ClimateLogoProps = {
    countrycode: CountryCode;
    color?: string;
    width?: number;
    height?: number;
    handleClick?: () => void;
    useNewLogo?: boolean;
};

// Explicit index.js importing is necessary because loadable full dynamic import relies on webpack
// dynamic importing, which by default tries to import and bundle on build everything in related folder
// including map files. However webpack does not have loaders for map files, which causes errors.
const Logo = loadable(
    (props: ClimateLogoProps) =>
        import(`@westwing/ui-kit/ClimateNeutralLogo/CountryLogos/${props.countrycode}/index.js`)
);

const ClimateNeutralLogo = ({
    countrycode,
    color = '#1D1D1F',
    width = 103,
    height = 22,
    handleClick,
    useNewLogo = false,
}: ClimateLogoProps): React.ReactElement => {
    let code: CountryCode = redirectedCountryCodes[countrycode] || countrycode;
    const t = useBellaTranslator();
    const config = useConfig();
    const carbonNeutral = t('carbon_neutral');
    const { availableCountryCodes, climateNeutralLogoUrl } = config;

    // Fallback to De if the country is not present, else this would blow up the website.
    if (!availableCountryCodes[code.toLowerCase()]) {
        code = 'De';
    }

    return (
        // eslint-disable-next-line react/forbid-elements
        <a
            target="_blank"
            rel="noopener noreferrer"
            aria-label={carbonNeutral}
            href={climateNeutralLogoUrl}
            onClick={handleClick}
        >
            <Logo
                useNewSvg={useNewLogo}
                countrycode={code}
                color={color}
                width={width}
                height={height}
                aria-hidden="true"
            />
        </a>
    );
};

export default ClimateNeutralLogo;
